import React from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { Text } from '@atlaskit/primitives/compiled';

import type { UpdateWorkItemAllFieldTypesAction } from '../../../../../common/types';
import { formatSuggestedDateTime } from '../../../../../common/utils/date-formatter-utils';
import { getActionData } from '../../../utils';

import type { dateTimePickerField_updateWorkItemAllFieldTypesActionRenderer$key } from './__generated__/dateTimePickerField_updateWorkItemAllFieldTypesActionRenderer.graphql';

const messages = defineMessages({
	setScheduledDate: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.date-picker-field.set-scheduled-date',
		defaultMessage: 'Set {name} to {value}',
		description: 'Label for the change action in the date picker field',
	},
	unset: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.date-picker-field.unset',
		defaultMessage: 'Remove date from {name}',
		description: 'Label for the change action in the date picker field',
	},
});

export type Props = {
	queryRef: dateTimePickerField_updateWorkItemAllFieldTypesActionRenderer$key;
	action: UpdateWorkItemAllFieldTypesAction;
};

export const DateTimePickerField = ({ queryRef, action }: Props): React.JSX.Element | null => {
	const { formatMessage, formatDate } = useIntl();

	const { fieldKey, fieldValue } = getActionData(action);

	const data = useFragment(
		graphql`
			fragment dateTimePickerField_updateWorkItemAllFieldTypesActionRenderer on JiraDateTimePickerField {
				fieldId
				name
			}
		`,
		queryRef,
	);

	if (!data?.fieldId || data?.fieldId !== fieldKey) {
		return null;
	}

	const rawValue = fieldValue;
	const value = formatSuggestedDateTime(rawValue, formatDate);

	return (
		<Text
			color="color.text.subtle"
			maxLines={1}
			testId="update-work-item-all-fields-action-renderer.ui.action-card.card-fields-list.date-time-picker-field"
		>
			{value
				? formatMessage(messages.setScheduledDate, { name: data?.name.toLowerCase(), value })
				: formatMessage(messages.unset, { name: data?.name.toLowerCase() })}
		</Text>
	);
};
